import { AfterViewInit, Component, computed, EventEmitter, Input, OnDestroy, Output, Signal, signal, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { Subscription } from 'rxjs';

import { VideogularComponent } from '../videogular/Videogular.Component';
import { ITimeMarker } from 'vbrick-player-src/videogular/controls/VbTimeMarkers.Component';
import { ActiveStats, C2PA_INVALID_ICON, C2PA_VERIFIED_ICON } from './IVbC2paContract';

import styles from './C2PA.Component.module.less';
import { IVbSource } from 'vbrick-player-src/IVbSource';
import { HlsUtilService } from 'vbrick-player-src/HlsUtil.Service';

@Component({
	selector: 'vb-c2pa',
	standalone: true,
	imports: [
		CommonModule,
		BsDropdownModule
	],
	template: `
		@if(showC2pa() && (isInvalidAsset || !isInvalidAsset && controlSurfaced)) {
			<div dropdown #bsDropdown class="dropdown">
				<button
					class="dropdown-toggle"
					[ngClass]="styles.c2paBtn"
					[attr.aria-label]="translations.c2paClickToViewDetails"
					type="button"
					dropdownToggle>
					<img [src]="!isInvalidAsset ? C2PA_VERIFIED_ICON : C2PA_INVALID_ICON" [alt]="!isInvalidAsset ? translations.c2paValid : translations.c2paInvalid" [ngClass]="styles.c2paLogo" />
				</button>
				<ul
					*dropdownMenu
					#vbDropdownMenu
					class="dropdown-menu keep-border-radius"
					[ngClass]="styles.dropdownMenu">
					<li class="dropdown-item" [ngClass]="styles.headerItem">
						<div [ngClass]="styles.title">
							<div>
								{{translations.c2paContentCredentials}}

								@if (isInvalidAsset) {
									<span [ngClass]="styles.invalid">({{translations.invalid}})</span>
								}
							</div>
							<div [ngClass]="styles.subtitle">{{videoStats()?.activeSegmentStats?.manifest?.signatureInfo?.issuer || 'Unknown'}}
							 / {{ videoStats()?.activeSegmentStats?.manifest?.signatureInfo?.time | date:'mediumDate' }}</div>
						</div>
					</li>
					@if (vgApi?.c2paConfig?.viewMoreClickHandler) {
						<li class="dropdown-item" [ngClass]="styles.item">
							<button
								[ngClass]="styles.viewMoreBtn"
								type="button"
								(click)="vgApi.c2paConfig?.viewMoreClickHandler?.()">
								{{ translations.viewMore }}
							</button>
						</li>
					}
				</ul>
			</div>
		}
	`,
	host: {
		'[class]': 'styles.host'
	}
})
export class VbC2PAComponent implements AfterViewInit, OnDestroy {
	@Input() public activeSource: IVbSource;
	@Input() public controlSurfaced: boolean;
	@Input() public translations: any;

	@Output() public failedC2paTimeMarkers: EventEmitter<ITimeMarker[]> = new EventEmitter();

	public videoStats = signal({} as ActiveStats);
	public audioStats = signal({} as ActiveStats);
	public showC2pa:Signal<boolean> = signal(false);

	public readonly styles = styles;
	public readonly C2PA_VERIFIED_ICON = C2PA_VERIFIED_ICON;
	public readonly C2PA_INVALID_ICON = C2PA_INVALID_ICON;
	public readonly
	public isInvalidAsset;
	private c2paStatReporterInstance;
	private subscription: Subscription;

	constructor(
		public vgApi: VideogularComponent,
		private HlsUtilService: HlsUtilService
	) {}


	public ngOnChanges(changes: SimpleChanges) {
		if (changes.activeSource) {
			this.initC2paStatsReporter();
		}
	}

	public ngAfterViewInit() {
		if(!this.vgApi?.c2paConfig) {
			return;
		}
		this.showC2pa = computed(() => {
			return !!(this.videoStats()?.activeSegmentStats || this.audioStats()?.activeSegmentStats);
		});
	}

	private initC2paStatsReporter(): void {
		if(!this.vgApi?.c2paConfig) {
			return;
		}
		const { src, type } = this.activeSource || {};
		import('./VbC2paStatsReporter').then(({ c2paStatReporterInstance }) => {
			this.c2paStatReporterInstance = c2paStatReporterInstance;
			this.c2paStatReporterInstance.destroy();

			//only incase of hls player current time needs to be fed to stats reporter.
			if (this.HlsUtilService.isHLS(src, type)) {
				this.c2paStatReporterInstance.initPlayerCurrentTimeObservable(this.vgApi.currentTime$);
			}

			this.subscription = c2paStatReporterInstance.currentStats$.subscribe((stats) => {
				this.updateC2paStats(stats);
			});
		});
	}

	private updateC2paStats(stats: ActiveStats): void {
		if (!stats) {
			this.videoStats.set({} as ActiveStats);
			this.audioStats.set({} as ActiveStats);
		}

		if (stats?.type === 'video') {
			this.videoStats.set(stats);
		} else if (stats?.type === 'audio') {
			this.audioStats.set(stats);
		}

		this.isInvalidAsset = stats && !stats.overallVerified;

		this.checkAndEmitFailedC2paTimeMarkers();

		this.vgApi.updateC2paStats({
			video: this.videoStats(),
			audio: this.audioStats()
		});
	}

	public ngOnDestroy() {
		this.c2paStatReporterInstance.destroy();
		this.subscription?.unsubscribe();
	}

	private checkAndEmitFailedC2paTimeMarkers(): void {
		if (this.isInvalidAsset) {
			const failedVideos = this.videoStats()?.errorSegments || [];
			const failedAudios = this.audioStats()?.errorSegments || [];
			const failedSegments = [...failedVideos, ...failedAudios];
			const timeMarkers: ITimeMarker[] = failedSegments.map(seg => ({
				startTime: seg.interval[0],
				endTime: seg.interval[1],
				cssClassName: styles.failedC2paMarker,
				markerOnScrubBar: true,
			}));
			this.failedC2paTimeMarkers.emit(timeMarkers);
		}
	}
}
